import React, { useRef, useEffect, useState, useMemo } from 'react';
import circleProfile from 'assets/img/icons/circle_profile.png';
import FollowUpQuestions from '../FollowUpQuestions';
import UserMessageActions from '../UserMessageActions';
import { UserData } from 'models/user/UserData.model';
import { ChatMessage } from 'models/user/research';
import { useResearchContext } from '../../ResearchContext.provider';
import SourcesView from '../SourcesView';
import { logWhenInDevMode } from 'utils/debug';
import './UserMessageView.scss';


export type UserMessageViewProps = {
  userProfile: UserData | null;
  message: ChatMessage;
  index: number;
  isLastQuestion: boolean;
  className?: string;
  stickyOffset?: number;
  isSticky?: boolean;
  hasScrollableArea?: boolean;
};

const UserMessageView = ({ 
  userProfile, 
  className = '',
  stickyOffset = 85,
  message,
  isLastQuestion = false,
  index,
  isSticky = false,
  hasScrollableArea = false
}: UserMessageViewProps) => {
  const { state, actions } = useResearchContext();
  const { longReportType, streaming, typing, imagesLoading, images,
    showMoreClicked, 
    followUpQuestions: followUpQuestionContent,
    routerReportType,
    isFuqLoading,
    reportFollowUpQuestions: reportFollowUpQuestionsMap,
  } = state;
  const [showFollowUpQuestions, setShowFollowUpQuestions] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const { 
    setChatMessage, 
    setMultiple, 
    generateFollowUpQuestions,
    onReportFollowUpClick,
    scrollToBottom,
    trackFeatureUsage
  } = actions;
  const [contentHeight, setContentHeight] = useState(0);
  const stickyRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const shouldShowButton = isLastQuestion && !typing && !streaming && !showMoreClicked && !imagesLoading;
  const hasFollowUpQuestions = !!(followUpQuestionContent?.followUpQuestions?.length > 0 || followUpQuestionContent?.hasError);
  const availableExpandReportType = useMemo(() => {
    if (routerReportType && reportFollowUpQuestionsMap?.[routerReportType]?.length) {
      return routerReportType;
    }
    if (longReportType && reportFollowUpQuestionsMap?.[longReportType]?.length) {
      return longReportType;
    }
    return null;
  }, [routerReportType, longReportType, reportFollowUpQuestionsMap]);

  const shouldShowExpandButton = useMemo(
    () => shouldShowButton && Boolean(availableExpandReportType),
    [shouldShowButton, availableExpandReportType]
  );

  const { sources: messageSources = [] } = message;
  
  // Fallback: extract sources from assistant response when message sources are empty
  const sources = useMemo(() => {
    if (messageSources.length > 0) return messageSources;
    
    // Get the assistant response that follows this user message
    const { chat } = state;
    const assistantMsg = chat[index + 1];
    if (!assistantMsg || assistantMsg.role !== 'assistant') return [];
    
    // Extract unique URLs from href attributes in the response
    const hrefMatches = assistantMsg.content?.match(/href=['"]?(https?:\/\/[^'"\s>]+)/g) || [];
    const uniqueUrls = Array.from(new Set(hrefMatches.map(m => m.replace(/href=['"]?/, ''))));
    
    return uniqueUrls.slice(0, 50).reduce<typeof messageSources>((acc, url) => {
      try {
        acc.push({
          url,
          title: new URL(url).hostname.replace('www.', ''),
          date: undefined,
          rawDate: undefined
        });
      } catch {}
      return acc;
    }, []);
  }, [messageSources, state.chat, index]);
  // useEffect(() => {
  //   logWhenInDevMode('UserMessageView', { isSticky });
  // }, [isSticky]);

  const handleFollowUpQuestionClick = (question: string) => {
    // In share mode, redirect to sign-up page
    if (state.isShareMode) {
      window.location.href = '/sign-up-senior';
      return;
    }
    setShowFollowUpQuestions(false);
    setChatMessage(question);
    setMultiple({ autocomplete: false });
    scrollToBottom(); 
  };

  const handleReportFollowUpClick = (question: string) => {
    onReportFollowUpClick?.(question, index);
    scrollToBottom();
  };

  // Use ResizeObserver to dynamically track height and position changes
  useEffect(() => {
    if (!isSticky) {
      if (showFollowUpQuestions) setShowFollowUpQuestions(false);
      setContentHeight(0);
      return;
    }

    if (!stickyRef.current || !containerRef.current) return;

    const updatePosition = () => {
      if (stickyRef.current && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const height = stickyRef.current.offsetHeight;
        
        setContentHeight(height);
        
        stickyRef.current.style.setProperty('--scroll-container-top', `${stickyOffset}px`);
        stickyRef.current.style.setProperty('--scroll-container-width', `${containerRect.width}px`);
        stickyRef.current.style.setProperty('--scroll-container-left', `${containerRect.left}px`);
      }
    };

    // Initialize position
    updatePosition();

    // Create ResizeObserver to track size changes (of the sticky box itself or the container)
    const resizeObserver = new ResizeObserver(() => {
      // Throttle/Debounce if necessary, but requestAnimationFrame is usually enough for visual sync
      requestAnimationFrame(updatePosition);
    });

    resizeObserver.observe(stickyRef.current);
    resizeObserver.observe(containerRef.current);
    
    // Also backup with window resize for cases where container might move without changing size
    window.addEventListener('resize', updatePosition);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updatePosition);
    };
  }, [isSticky, stickyOffset]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (showFollowUpQuestions) {
          setShowFollowUpQuestions(false);
        }
        if (showSources) {
          setShowSources(false);
        }
      }
    };

    if (showFollowUpQuestions || showSources) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFollowUpQuestions, showSources]);

  return (
    <div 
      ref={containerRef}
      className={`user-message-view ${className || ''}`}
      style={{ minHeight: isSticky ? `${contentHeight}px` : 'auto' }}
    >
      <div 
        ref={stickyRef}
        className={`user-message-view__sticky-container ${isSticky ? 'is-sticky' : ''} ${!hasScrollableArea ? 'has-no-scrollable-area' : ''}`}
        style={{ top: isSticky ? `${stickyOffset}px` : undefined }}
      >
        <div className="user-message-view__profile">
          {userProfile?.avatar && (
            <img 
              src={userProfile.avatar} 
              alt={userProfile.name}
              className="user-message-view__avatar"
            />
          )}
          {!userProfile?.avatar && (
            <img src={circleProfile} className="user-message-view__avatar"/>
          )}
          { userProfile && (
          <div className="user-message-view__user-info">
            <h3 className="user-message-view__name">{userProfile?.firstName} {userProfile?.lastName}</h3>
            {userProfile?.title && (
              <p className="user-message-view__title">{userProfile.title}</p>
            )}
          </div>
          )}
          { !userProfile && (
            <div className="user-message-view__user-info">
              <h3 className="user-message-view__name">You</h3>
            </div>
          )}
        </div>

        <div className="user-message-view__question">
          <p className="user-message-view__question-text" dangerouslySetInnerHTML={{ __html: message.content }} />
        </div>
        <UserMessageActions
          shouldShowButton={shouldShowButton}
          isLastQuestion={isLastQuestion}
          index={index}
          hasImages={(index + 1) in images}
          hasFollowUpQuestions={hasFollowUpQuestions}
          showFollowUpQuestions={showFollowUpQuestions}
          sourcesLength={sources.length}
          showSources={showSources}
          shouldShowExpandButton={shouldShowExpandButton}
          onReportFollowUpClick ={handleReportFollowUpClick}
          onToggleFollowUpQuestions={() => { 
            if (!showFollowUpQuestions) trackFeatureUsage?.('deepDive');
            setShowFollowUpQuestions(!showFollowUpQuestions); 
            setShowSources(false); 
          }}
          onToggleSources={() => { setShowSources(!showSources); setShowFollowUpQuestions(false); }}
        />
        <div className={`user-message-view__followup-questions ${(isLastQuestion && hasFollowUpQuestions && !typing && !streaming && showFollowUpQuestions) ? 'open' : ''}`}>
          <FollowUpQuestions 
            followUpQuestionContent={followUpQuestionContent} 
            isLoading={isFuqLoading}
            onRefresh={() => {
              // Same guard as handleFollowUpQuestionClick above - this hits
              // /api/nlp/followUp, which requires a real session.
              if (state.isShareMode) {
                window.location.href = '/sign-up-senior';
                return;
              }
              generateFollowUpQuestions();
            }}
            onQuestionClick={(q, i) => handleFollowUpQuestionClick?.(q)} 
            title="You might also want to ask:" 
          />
        </div>
        <div className={`user-message-view__sources ${(showSources && sources.length > 0) ? 'open' : ''}`}>
           { sources.length > 0 && <SourcesView sources={sources} /> }
        </div>
      </div>
    </div>
  );
};


export default UserMessageView;
